import { memo, useCallback, useRef, useState } from 'react';
import { Box, Text, Button } from '@bonanza-social/moonlight';
import { useSelector } from '@bonanza-social/toolkit/state';
import { selectAvailableGames } from '../../state';
import { Game } from '../../state/shared/games.types';
import { GamesRandomizerProps } from './GamesRandomizer.types';
import { GamesRandomizerColumnsExposedProps } from './GamesRandomizerColumns/GamesRandomizerColumns.types';
import { GamesRandomizerSelectionExposedProps } from './GamesRandomizerSelection/GamesRandomizerSelection.types';
import GamesRandomizerSelection from './GamesRandomizerSelection/GamesRandomizerSelection';
import GamesRandomizerColumns from './GamesRandomizerColumns/GamesRandomizerColumns';

function GamesRandomizer(props: GamesRandomizerProps) {
  const [targetGameId, setTargetGameId] = useState<Game['id']>();
  const [loadingImages, setLoadingImages] = useState(false);
  const games = useSelector(selectAvailableGames);
  const columnRefs = useRef<GamesRandomizerColumnsExposedProps>(null);
  const selectionRef = useRef<GamesRandomizerSelectionExposedProps>(null);

  const onSpin = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * games.length);
    const randomId = games[randomIndex].id;

    setLoadingImages(true);

    columnRefs.current?.onChoosingStart(randomId, () => {
      selectionRef.current?.show(() => {
        columnRefs.current?.onChoosingStop();
      });
    });

    setLoadingImages(false);
    setTargetGameId(randomId);
  }, [games]);

  const onRespin = useCallback(() => {
    selectionRef.current?.hide(onSpin);
  }, [onSpin]);

  return !games.length ? null : (
    <Box variant="gradient" borderRadius="radius-400" {...props}>
      <Box
        gap="space-500"
        paddingVertical="space-600"
        paddingHorizontal="space-400"
      >
        <Text
          variant="h5"
          locNS="games"
          locKey="randomizerChooseTitle"
          color="accentSurfacePrimary"
          textAlign="center"
        />
        <GamesRandomizerColumns ref={columnRefs} targetGameId={targetGameId} />
        <Button
          locNS="games"
          locKey="randomizerSurpriseMe"
          marginHorizontal="space-600"
          alignSelf="center"
          loading={loadingImages}
          disabled={targetGameId !== undefined}
          width="70%"
          maxWidth={330}
          onPress={onSpin}
          light
        />
      </Box>
      <GamesRandomizerSelection
        ref={selectionRef}
        targetGameId={targetGameId}
        onButtonPress={onRespin}
      />
    </Box>
  );
}

export default memo(GamesRandomizer);
